import {h, Fragment} from "preact"

import { useState, useEffect } from "preact/hooks";
import { SerpElement, useCurrencyFormatting } from "@nosto/preact";
import ProductBadge from "./ProductBadge";
import ProductColorCountBadge from "./ProductColorCountBadge";
import { extractShopifyHandle } from "./utils/extractShopifyHandle";
import { translatedText } from "./utils/translatedText";


function setUrl(fullUrl) {
  // Normalize root to not have leading or trailing slashes for later concatenation
  const root = window?.Shopify?.routes?.root || "/";
  const cleanRoot = root.replace(/^\/|\/$/g, "");

  // Try to extract path starting from `/products/...`
  const match = fullUrl.match(/\/products\/.+$/);
  const productPath = match
    ? match[0]
    : (() => {
        // fallback: slice from 4th segment onward
        const parts = fullUrl.split("/");
        return "/" + parts.slice(3).join("/");
      })();

  return cleanRoot ? `/${cleanRoot}${productPath}` : productPath;
}
  
function handleQuickShopClick(url) {
  const shortUrl = setUrl(url);

  window.dispatchEvent(new CustomEvent("quickshop", { detail: shortUrl }));
  window.dispatchEvent(new Event("closemodal"));
  window.dispatchEvent(new Event("cart-hide"));

  if (typeof Tracking?.quickShopClick === "function") {
    Tracking.quickShopClick("quickshop-");
  }
} 

export default function Product({ product, index}) {
  const [hover, setHover] = useState(false);
  const { format } = useCurrencyFormatting();

  // --- IMAGE SOURCES ---
  const alternateImageUrls = Array.isArray(product.alternateImageUrls) ? product.alternateImageUrls : [];
  const baseProductImage = product.imageUrl || "";
  const customFields = product.customFields || [];

  const selectAltImage = (key) => (key ? (alternateImageUrls.find((u) => u.includes(key)) || "") : "");
  const getFieldKey = (field) => (customFields.find((f) => f.key === field)?.value || "").trim().replace(".", "");
  const isOverrideKeyValid = (key) => key && key !== "No override" && !key.endsWith(".mp4");
  const has003 = (u) => typeof u === "string" && u.includes("_003");

  const resolveImage = ({ overrideKey, defaultImage }) => {
    const overrideImage = isOverrideKeyValid(overrideKey) ? (overrideKey === "_001" ? baseProductImage : selectAltImage(overrideKey)) : "";
    return overrideImage || defaultImage;
  };

  /* Featured */
  const featuredOverrideKey = getFieldKey("custom-featured_image_override");
  const featuredDefaultImage = selectAltImage("_003") || (baseProductImage.includes("_003") ? baseProductImage : "") || "";
  const featuredCandidateImage = resolveImage({ overrideKey: featuredOverrideKey, defaultImage: featuredDefaultImage });

  /* Hover */
  const hoverOverrideKey = getFieldKey("custom-hover_image_override");
  const hoverDefaultImage = selectAltImage("_001") || (baseProductImage.includes("_001") ? baseProductImage : "") || "";
  const hoverImage = resolveImage({ overrideKey: hoverOverrideKey, defaultImage: hoverDefaultImage });
  
  /* Final */
  const featuredImage = featuredCandidateImage && featuredCandidateImage !== hoverImage ? featuredCandidateImage : null;
  const featuredImageBackground = !has003(featuredImage);
  const hoverImageBackground = !has003(hoverImage);

  return (
    <li 
      className="nosto-product-info grid__item"
      x-nosto-handle={extractShopifyHandle(product?.url)}
    > 
      <SerpElement
        as="a"
        hit={product}
        ariaLabel={`Product ${product.name}`}
        className="nosto-product-url product-link relative card-product grid gap-12"
      >
        <div
          className={`relative overflow-hidden aspect-2-3 bg-cover bg-center ${featuredImageBackground || hoverImageBackground? 'bg-productImgBg':''}`} 
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <ProductBadge product={product} />

          {featuredImage && hoverImage ? (
            <>
              <img
                src={featuredImage}
                alt={product.name}
                className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-300 ${ hover ? "opacity-0" : "opacity-100"} ${ featuredImageBackground ? "mix-blend-multiply" : ""}`}
              />
              <img
                src={hoverImage || baseProductImage}
                alt={`${product.name} alternate view`}
                className={`absolute inset-0 w-full h-full object-cover object-center hover-fade-in z-10 ${ hover ? "" : "opacity-0"} ${ hoverImageBackground ? "mix-blend-multiply" : ""}`} 
              />
            </>
          ) : (
            <img
              src={hoverImage}
              alt={product.name}
              className={`absolute inset-0 w-full h-full object-cover object-center hover-fade-in z-10 ${ hoverImageBackground ? "mix-blend-multiply" : ""}`}
            />
          )}

          <div class="hidden card-product-color-badge-wrapper infobox lg:block">
            <div class="card-product-color-amount-wrapper ">
              <div class="absolute bottom-12 left-12">
                <ProductColorCountBadge product={product} />
              </div>
            </div>
          </div>
          
          <div class={`fibbl-bar-wrapper-${product.productId || index} fibbl-bar-wrapper-10 infobox absolute pointer-events-none max-lg:!left-0 z-10 !bottom-0 lg:!right-0`}>
            <fibbl-bar
              data-product-id={`${extractShopifyHandle(product?.url)}`}
              class="bg-transparent gap-12 px-8 lg:px-12 pt-8 pb-12"
            >
              <button
                data-element="fibbl-3d-quick-view"
                class="fibbl-button-primary pointer-events-auto plp"
              >
                <span>3D</span>
              </button>

              <button
                data-element="fibbl-qr-code"
                data-type="vto"
                class="fibbl-button-secondary pointer-events-auto whitespace-nowrap"
              >
                <span>
                {translatedText("tryOn")}
                </span>
              </button>
            </fibbl-bar> 
          </div>
        </div>

        <div className="infobox below-card-infobox-1 relative flex gap-y-8 gap-x-16" data-nosto-element="product">
            <div className="card-product-text grid gap-4 text-13-120--0.5-700 w-full">
                <span className="lg:col-span-6 normal-case">{product.name?.split(" / ")[0]}</span>
                <div aria-label="Price" class="nosto-product-price price__sale flex gap-8">
                  <span class="nosto-newprice money">{format(product.price)}</span>
                  {product.listPrice && product.listPrice > product.price && (
                      <s class="nosto-oldprice money price-item price-item--regular opacity-[.55]">{format(product.listPrice)}</s>
                  )}
                </div>
                <div class="card-product-color-amount-wrapper lg:!hidden">
                  <ProductColorCountBadge product={product} />
                </div>
            </div>

            <span  
            className="flex px-6 pb-8" 
            data-scroll-label="Quick shop" 
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleQuickShopClick(product.url);
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="19" viewBox="0 0 13 19" fill="none">
                <path stroke="black" stroke-width="1.2" d="M12.4 5.1v12.8H.6V5.1h11.8ZM6.5 9v5M4 11.5h5M9.5 4.5a3 3 0 0 0-6 0"></path>
              </svg>
            </span>
        </div>
      </SerpElement>
    </li>
  );
}
